import { IconButton } from "@wealthx/ui";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CloseIcon from "@mui/icons-material/Close";
import { CssBaseline, StyledEngineProvider } from "@mui/material";
import { useLocation } from "@reach/router";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";
import { navigate } from "gatsby";
import { SnackbarProvider } from "notistack";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import axiosClient, { checkIsPrivateRoute } from "../../api-services/axiosClient";
import { INITIAL_APP_STATE, NOT_DASHBOARD_ROUTES } from "../../commons/enum";
import { APP_LAYOUT_TYPES } from "../../commons/layout-types.constants";
import { AIChatProvider, useAIChat } from "../../context/ai-chat-context.tsx";
import DataLoadingContext from "../../context/data-loading-context";
import "../../fonts/font.css";
import { useIsAuthenticatedRedirectPages, useIsTablet } from "../../hooks/common.hooks";
import { useCompanyInfo } from "../../hooks/company.hooks.ts";
import "../../features/account/scss/account.scss";
import "../../scss/common.scss";
import "../../pages/contact/scss/contact.scss";
import "../../scss/layout.scss";
import "../../scss/auth-layout.scss";
import { getCsrfToken, logout } from "../../utils/auth";
import { DEFAULT_PRIMARY, DEFAULT_SECONDARY } from "../../utils/theme-utils.ts";
import { currentSession } from "../../utils/user-auth-provider";
import SEO from "../SEO";
import SendingStatusAlert from "../SendingStatusAlert/index.tsx";
import StaffImportBanner from "../StaffImportBanner/index.tsx";
import SideBar from "../SideBar";
import TwoFASnackbar from "../TwoFASnackbar/index.tsx";
import { AuthenticatedRedirect } from "../authenticated-redirect";
import LoadingBackdrop from "../loading-backdrop";
import SharingLayout from "../sharing-layout";
import SignupBanner from "../signup-banner-left";
import { DashboardLogicWrapper } from "./dashboard-logic-wrapper";
import SignupLayout from "./signup-layout";

import UserAuthenticationNavigate from "./user-authentication-navigate";
import AIChatSidePanel from "../AIChatSidePanel/index.tsx";
import SentryIdentity from "../SentryIdentity";
import { useTenantTheme } from "../../context/tenant-theme-context.tsx";

const GlobalAIChat = () => {
  const { isOpen, close } = useAIChat();
  return <AIChatSidePanel isOpen={isOpen} onClose={close} />;
};

axiosClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error?.response) {
      if (
        error.response.status === 401 &&
        error.response.data?.message === "Unauthorized" &&
        error.config.url.indexOf("logout") < 0
      ) {
        try {
          const session = await currentSession();
          const apiConfig = {
            ...error.config,
            headers: {
              ...error.config.headers,
              authentication: session.tokens.accessToken.toString(),
            },
          };
          axiosClient.defaults.headers.common.authentication =
            session.tokens.accessToken.toString();
          return axiosClient(apiConfig);
        } catch (err) {
          if (err === "No current user" && checkIsPrivateRoute()) {
            logout();
          }
        }
      }
      if (error.response.status === 404 && error.response.data?.message?.includes("staff alias")) {
        navigate("/404");
      }
      if (
        error.response.status === 403 &&
        error.response.data?.message?.includes("not allowed to access")
      ) {
        navigate("/forbidden");
      }
      if (error.response.status === 403 && error.response.data?.errorCode === "CFRSTOKEN_EXPIRED") {
        const retryCount = error.config._csrfRetryCount || 0;
        if (retryCount >= 3) {
          throw error.response;
        }
        const csrfToken = await getCsrfToken();
        const apiConfig = {
          ...error.config,
          _csrfRetryCount: retryCount + 1,
          headers: {
            ...error.config.headers,
            "csrf-token": csrfToken,
          },
        };
        axiosClient.defaults.headers.common["csrf-token"] = csrfToken;
        return axiosClient(apiConfig);
      }
      throw error.response;
    }
    throw error;
  }
);

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.GATSBY_COGNITO_USER_POOL_ID,
      userPoolClientId: process.env.GATSBY_COGNITO_APP_CLIENT_ID,
    },
  },
});
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: process.env.GATSBY_SHARED_COOKIE_DOMAIN,
    secure: !(process.env.GATSBY_SHARED_COOKIE_DOMAIN || "").includes("localhost"),
    path: "/",
    expires: 365,
    sameSite: "lax",
  })
);

const cache = createCache({
  key: "experity",
  prepend: true,
});

const getStatusDataReducer = (state, action) => {
  switch (action.type) {
    case "appState":
      return { ...state, ...action.value };
    default:
      throw new Error("Unexpected action");
  }
};

const initializeApp = async (pathname) => {
  try {
    await getCsrfToken();
  } catch {
    const isInDashboardRoutes = NOT_DASHBOARD_ROUTES.every((item) => pathname.indexOf(item) < 0);
    if (isInDashboardRoutes) {
      navigate("/login");
    }
  }
};
const SnackBarActionItem = ({ handleClose = () => {} }) => {
  return (
    <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
      <CloseIcon fontSize="small" />
    </IconButton>
  );
};

const Layout = ({ children, location, pageContext }) => {
  const [appState, appStateDispatch] = useReducer(getStatusDataReducer, INITIAL_APP_STATE);
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const localStorageData = JSON.parse(decodeURIComponent(urlParams.get("data")));
  const setAppState = (data) => appStateDispatch({ type: "appState", value: data });
  const [isShowIntercom, setIsShowIntercom] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const {
    data: companyData,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    isFetched: isCompanyFetched,
  } = useCompanyInfo();

  const { setColors } = useTenantTheme();

  useEffect(() => {
    if (companyData?.theme) {
      setColors({
        primary: companyData.theme.primaryColor ?? DEFAULT_PRIMARY,
        secondary: companyData.theme.secondaryColor ?? DEFAULT_SECONDARY,
      });
    }
  }, [companyData?.theme?.primaryColor, companyData?.theme?.secondaryColor, setColors]);

  const isTablet = useIsTablet();

  const pathname = location.pathname.replace(/\//g, "");
  const isAuthenticatedRedirectPages = useIsAuthenticatedRedirectPages(pathname);
  const pageLayoutRef = useRef(null);

  const notistackRef = useRef();
  const onClickDismiss = useCallback(
    (key) => () => {
      notistackRef.current.closeSnackbar(key);
    },
    []
  );

  const renderSnackbarAction = useCallback(
    (key) => {
      const handleClose = onClickDismiss(key);
      return <SnackBarActionItem handleClose={handleClose} />;
    },
    [onClickDismiss]
  );

  if (typeof window !== "undefined") {
    const vh = window.innerHeight * 0.01;
    pageLayoutRef.current?.style.setProperty("--vh", `${vh}px`);
  }
  const onResizeHandler = () => {
    const vh = window.innerHeight * 0.01;
    pageLayoutRef.current?.style.setProperty("--vh", `${vh}px`);
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("resize", onResizeHandler);
    }
    /**
     * * Cleanup function to remove event listener on unmount
     * * This is crucial for React 18 Strict Mode which may mount/unmount components multiple times
     */
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", onResizeHandler);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (localStorageData) {
      const entries = Object.entries(localStorageData);
      entries.forEach(([key, value]) => {
        localStorage.setItem(key, value);
      });
    }
    setIsInitializing(true);
    initializeApp(location.pathname).finally(() => {
      setIsInitializing(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataLoadingContextValue = useMemo(
    () => ({
      ...appState,
      setAppState,
      setIsShowIntercom,
      isShowIntercom,
    }),
    [appState, setAppState, setIsShowIntercom, isShowIntercom]
  );

  const renderPage = () => {
    switch (pageContext.layout) {
      case APP_LAYOUT_TYPES.LOGIN_LAYOUT: {
        if ((isCompanyLoading && !isCompanyFetched) || isCompanyError) {
          return <LoadingBackdrop />;
        }
        const page = (
          <div className="page-root__body" ref={pageLayoutRef}>
            <div className="login-wrapper">
              {!isTablet && <SignupBanner />}
              {children}
            </div>
          </div>
        );

        if (isAuthenticatedRedirectPages) {
          return (
            <UserAuthenticationNavigate location={location}>
              <AuthenticatedRedirect redirectTo="/">{page}</AuthenticatedRedirect>
            </UserAuthenticationNavigate>
          );
        }
        return page;
      }
      case APP_LAYOUT_TYPES.SHARING_LAYOUT: {
        return <SharingLayout>{children}</SharingLayout>;
      }
      case APP_LAYOUT_TYPES.SIGNUP_LAYOUT: {
        return <SignupLayout>{children}</SignupLayout>;
      }
      case APP_LAYOUT_TYPES.ERROR_LAYOUT: {
        return children;
      }
      case APP_LAYOUT_TYPES.TEMPLATE_PREVIEW_LAYOUT: {
        return (
          <UserAuthenticationNavigate location={location}>{children}</UserAuthenticationNavigate>
        );
      }
      default: {
        if (isCompanyLoading && !isCompanyFetched) {
          return <LoadingBackdrop />;
        }
        return (
          <UserAuthenticationNavigate location={location}>
            <DashboardLogicWrapper>
              <div className="page-root__container">
                <div className="page-root__side-bar">
                  <SideBar location={location} />
                </div>
                <div className="page-root__body" ref={pageLayoutRef}>
                  <StaffImportBanner />
                  <SendingStatusAlert />
                  <TwoFASnackbar />
                  <div className="page-root__body-content">{children}</div>
                </div>
                {isShowIntercom && null}
              </div>
            </DashboardLogicWrapper>
          </UserAuthenticationNavigate>
        );
      }
    }
  };

  if (isInitializing || (isCompanyLoading && !isCompanyFetched)) {
    return <LoadingBackdrop />;
  }

  return (
    <AIChatProvider>
      <DataLoadingContext.Provider value={dataLoadingContextValue}>
        <SentryIdentity />
        <SnackbarProvider
          ref={notistackRef}
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          autoHideDuration={2000}
          action={renderSnackbarAction}
          maxSnack={2}
        >
          <CssBaseline />
          <SEO />
          <StyledEngineProvider injectFirst>
            <CacheProvider value={cache}>{renderPage()}</CacheProvider>
          </StyledEngineProvider>
          <GlobalAIChat />
        </SnackbarProvider>
      </DataLoadingContext.Provider>
    </AIChatProvider>
  );
};

export default Layout;
